import React, { useState, useEffect, useMemo, useCallback } from "react";
import EditableTable from "../../../../Controllers/EditableTable";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Grid, Paper, Typography } from "@mui/material";
import service from "./../../helpers/GifService";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  canEditSelector,
  isLeaderSelector,
  applicantSelector,
} from "../../../../store/applicantsSlice";
import { useSelector } from "react-redux";
import { useLoading } from "../../../../providers/loadingProvider";
import { useNotify } from "../../../../providers/NotificationProvider";
import {
  colleaguesSelector,
  proposalSelector,
} from "../../../../store/proposalSlice";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
    overflowX: "auto",
  },
  table: {
    minWidth: 650,
  },
  infoIcon: {
    marginLeft: 5,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
}));

export default function PreviousEmployment() {
  const classes = useStyles();
  const loading = useLoading();
  const notify = useNotify();
  const colleagues = useSelector(colleaguesSelector);
  const proposal = useSelector(proposalSelector);
  const isLeader = useSelector(isLeaderSelector);
  const applicant = useSelector(applicantSelector);
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [researchAreaOptions, setResearchAreaOptions] = useState([]);
  const [previousEmployments, setPreviousEmployments] = useState([]);
  const [proposalApplicants, setProposalApplicants] = useState([]);
  const canEdit = useSelector(canEditSelector);

  const validateRow = (row) => {
    let errorList = [];
    if (row.from === null || row.from === undefined || row.from === "")
      errorList.push("From");
    if (row.to === null || row.to === undefined || row.to === "")
      errorList.push("To");
    if (
      row.institutionName === null ||
      row.institutionName === undefined ||
      row.institutionName === ""
    )
      errorList.push("Institution Name");
    if (
      row.researchareaId === null ||
      row.researchareaId === undefined ||
      row.researchareaId === ""
    )
      errorList.push("Research Area");
    if (row.title === null || row.title === undefined || row.title === "")
      errorList.push("Title");

    if (errorList.length === 0) return true;

    notify.show(`Missing Fields:  ${errorList.join(", ")}`, {
      severity: "error",
    });

    return false;
  };

  const setRow = (row) => {
    const loadingId = loading.addLoading("saving previous employment");
    service
      .updateApplicantPreviousEmployment({
        previousEmployment: row,
      })
      .then(() => {
        loading.removeLoading(loadingId);
        getAllPreviousEmployment();
      });
  };

  const addRow = async (row) => {
    const loadingId = loading.addLoading("adding previous employment");
    const data = { proposalid: proposal.id, ...row };
    service
      .createApplicantPreviousEmployment({ previousEmployment: data })
      .then(() => {
        loading.removeLoading(loadingId);
        getAllPreviousEmployment();
      });
  };

  const deleteRow = (previousEmploymentsId) => {
    const loadingId = loading.addLoading("removing previous employment");
    service.DeactivatePreviousEmployment({ previousEmploymentsId }).then(() => {
      loading.removeLoading(loadingId);
      getAllPreviousEmployment();
    });
  };

  const fillInstitutionsOptions = useCallback(async () => {
    const loadingId = loading.addLoading("loading institutions");
    var institutions = await service.getAllInstitutions(false);
    setInstitutionOptions(
      institutions.map((i) => ({ ...i, id: i.name, label: i.name }))
    );
    loading.removeLoading(loadingId);
  }, [loading]);

  const fillResearchAreaOptions = useCallback(async () => {
    const loadingId = loading.addLoading("loading research areas");
    var researchAreas = await service.getResearchAreas();
    setResearchAreaOptions(
      researchAreas.map((r) => ({ ...r, id: r.researchAreaId, label: r.name }))
    );
    loading.removeLoading(loadingId);
  }, [loading]);

  const getAllPreviousEmployment = useCallback(async () => {
    const loadingId = loading.addLoading("loading previous employments");
    let preEmp = await service.GetApplicantPreviousEmployment();
    preEmp.sort((a, b) => new Date(a.from) - new Date(b.from));
    setPreviousEmployments(preEmp);
    loading.removeLoading(loadingId);
  }, [loading]);

  const getProposalApplicants = useCallback(async () => {
    if (!colleagues) return [];
    return colleagues.map((c) => ({
      id: c.id,
      name: c.name,
    }));
  }, [colleagues]);

  useEffect(() => {
    const getData = async () => {
      fillResearchAreaOptions();
      fillInstitutionsOptions();
      getAllPreviousEmployment();
      const applicants = await getProposalApplicants();
      setProposalApplicants(applicants.map((a) => ({ ...a, label: a.name })));
    };
    getData();
  }, [
    fillInstitutionsOptions,
    fillResearchAreaOptions,
    getAllPreviousEmployment,
    getProposalApplicants,
  ]);

  const tableConfig = useMemo(() => {
    let leaderColumns = [];
    if (isLeader) {
      leaderColumns = [
        {
          id: "applicantId",
          label: "Name",
          field: "applicantId",
          type: "options",
          options: proposalApplicants,
          width: 3,
          skipValidation: true,
          value: applicant.id,
        },
      ];
    }
    return {
      idField: "id",
      disableEdit: !canEdit,
      columns: [
        ...leaderColumns,
        {
          id: "from",
          label: "From",
          field: "from",
          type: "date",
        },
        {
          id: "to",
          label: "To",
          field: "to",
          type: "date",
        },
        {
          id: "institutionName",
          label: "Institution Name",
          field: "institutionName",
          type: "options",
          options: institutionOptions,
          width: 3,
        },
        {
          id: "researchareaId",
          label: "Research Area",
          field: "researchareaId",
          type: "options",
          options: researchAreaOptions,
          width: 2,
        },
        {
          id: "title",
          label: "Title",
          field: "title",
          width: 2,
        },
      ],
    };
  }, [
    isLeader,
    canEdit,
    institutionOptions,
    researchAreaOptions,
    proposalApplicants,
    applicant.id,
  ]);

  return (
    <>
      <Grid container>
        <Box className={classes.header}>
          <Typography variant="subtitle2">Previous Employment</Typography>
          <InfoOutlinedIcon className={classes.infoIcon} color="primary" />
        </Box>
      </Grid>
      <Paper className={classes.root} elevation={0}>
        <EditableTable
          tableConfig={tableConfig}
          tableData={previousEmployments}
          rowValidate={validateRow}
          rowCreated={addRow}
          rowUpdated={setRow}
          rowDeleted={deleteRow}
        />
      </Paper>
    </>
  );
}
